import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, AlertCircle, KeyRound, LogOut } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from "@/integrations/supabase/client";
import Loader from "@/components/ui/loader";
import { TRAVEL_VIDEOS } from "@/components/utils/assets";
import { cn } from "@/components/utils/utils";
import PasswordFields, { isPasswordValid, type PasswordData } from "@/components/ui/password-fields";

const SetPassword = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { signOut, refreshUserData } = useAuth();

  // Video background state (same as Login)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const travelVideos = TRAVEL_VIDEOS;

  const handleVideoEnd = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % travelVideos.length);
      setIsTransitioning(false);
    }, 500);
  };

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideoIndex) {
          video.currentTime = 0;
          video.play().catch(console.error);
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentVideoIndex]);

  useEffect(() => {
    if (videoRefs.current[0]) {
      videoRefs.current[0].play().catch(console.error);
    }
  }, []);

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordData>({ password: '', confirmPassword: '' });

  const allValid = isPasswordValid(passwordData);

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Con HashRouter l'URL risultante è: #/set-password#access_token=xxx&refresh_token=yyy
        // Il doppio # fa sì che Supabase non riesca a parsare i token automaticamente.
        // Li estraiamo manualmente e inizializziamo la sessione.
        const hash = window.location.hash;
        const accessTokenMatch = hash.match(/access_token=([^&]+)/);
        const refreshTokenMatch = hash.match(/refresh_token=([^&]+)/);

        if (accessTokenMatch && refreshTokenMatch) {
          const { error } = await supabase.auth.setSession({
            access_token: accessTokenMatch[1],
            refresh_token: refreshTokenMatch[1],
          });
          if (error) { setIsLinkInvalid(true); setError(t('setPassword.sessionCheckError')); return; }
          return; // sessione inizializzata, il form può procedere
        }

        // Fallback: sessione già presente (es. refresh della pagina)
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) { setIsLinkInvalid(true); setError(t('setPassword.sessionCheckError')); return; }
        if (!session) { setIsLinkInvalid(true); setError(t('setPassword.linkExpired')); return; }
      } catch {
        setIsLinkInvalid(true); setError(t('setPassword.verificationError'));
      }
    };
    checkSession();
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allValid) {
      setError(t('setPassword.enterBoth'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({ password: passwordData.password });

      if (error) {
        let errorMessage = t('setPassword.updateError');
        if (error.message.includes("Password should be at least")) errorMessage = t('setPassword.passwordMinLength');
        else if (error.message.includes("Invalid token")) errorMessage = t('setPassword.linkExpired');
        else if (error.message.includes("User not found")) errorMessage = t('setPassword.userNotFound');
        setError(errorMessage);
      } else {
        // Il trigger DB aggiorna approval_status da 'reset-psw' → 'approved'.
        // Forziamo il re-fetch del profilo prima di navigare, altrimenti
        // il contesto ha ancora userStatus='reset-psw' e ApprovalRoute rimanda qui.
        await refreshUserData();
        setIsSuccess(true);
      }
    } catch {
      setError(t('setPassword.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left side — hero video (same as Login) */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0">
          {travelVideos.map((video, index) => (
            <video
              key={`${video}-${index}`}
              ref={(el) => (videoRefs.current[index] = el)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentVideoIndex ? 'opacity-100' : 'opacity-0'}`}
              muted
              loop={false}
              playsInline
              onEnded={index === currentVideoIndex ? handleVideoEnd : undefined}
            >
              <source src={video} type="video/mp4" />
            </video>
          ))}
          {isTransitioning && (
            <div className="absolute inset-0 bg-black/20 transition-opacity duration-500 ease-in-out" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#030A14]/80 via-[#0A1525]/70 to-[#142536]/60" />
        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full px-12 lg:px-16 text-center">
          <div className="max-w-2xl">
            <h1 className="text-white mb-10">
              {t('login.heroTitle')}
              <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                {t('login.heroTitleColored')}
              </span>
            </h1>
            <p className="text-md lg:text-lg text-white/90 mb-20 max-w-lg mx-auto">
              {t('login.heroSubtitle')}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030A14] to-transparent" />
      </div>

      {/* Right side — set password form */}
      <div className={cn("bg-white shadow-2xl flex flex-col h-screen", "w-full lg:max-w-md")}>
        <div className="flex flex-col h-full">

          {/* Header with logo */}
          <div className="px-4 lg:px-6 pt-4 lg:pt-6 pb-4 border-b border-gray-100">
            <div className="flex justify-center">
              <img
                src="/assets/images/dark_extended.png"
                alt="Place2Be Logo"
                className="[@media(max-height:640px)]:w-20 [@media(max-height:640px)]:h-6 w-28 lg:w-48 h-8 lg:h-16 object-contain drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Scrollable form area */}
          <ScrollArea className="flex-1 px-4 lg:px-6 pt-6">
            {isLinkInvalid ? (
              <div className="flex flex-col items-center text-center gap-4 py-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-gray-900">{t('setPassword.linkExpiredTitle')}</h2>
                <p className="text-gray-600">{error}</p>
                <Button onClick={() => navigate('/login')} variant="ghost" className="text-gray-600 hover:text-gray-800">
                  {t('setPassword.backToLogin')}
                </Button>
              </div>
            ) : isSuccess ? (
              <div className="flex flex-col items-center text-center gap-6 py-10">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-gray-900">You're all set! 🎉</h2>
                  <p className="text-gray-500">Everything's ready. You can start exploring the platform now.</p>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-8 bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium"
                >
                  Start exploring
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center gap-4">
                  <div className="w-12 h-12 [@media(max-height:640px)]:w-8 [@media(max-height:640px)]:h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <KeyRound className="w-6 h-6 [@media(max-height:640px)]:w-4 [@media(max-height:640px)]:h-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="[@media(max-height:640px)]:font-bold text-gray-900">{t('setPassword.title')}</h2>
                    <p className="[@media(max-height:640px)]:text-sm text-gray-600 mt-0.5">{t('setPassword.desc')}</p>
                  </div>
                </div>

                {error && (
                  <Alert className="mb-6 border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSetPassword} className="space-y-4">
                  <PasswordFields
                    value={passwordData}
                    onChange={setPasswordData}
                    disabled={isLoading}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium disabled:opacity-50"
                    disabled={isLoading || !allValid}
                  >
                    {isLoading ? (
                      <><Loader size="sm" className="mr-3" />{t('setPassword.setting')}</>
                    ) : (
                      <><KeyRound className="w-5 h-5 mr-2" />{t('setPassword.setPasswordButton')}</>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                </div>
              </>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 lg:px-6 py-4 border-t border-gray-100 bg-gray-50/50 space-y-3">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              By continuing you agree to our{" "}
              <a href="/terms" className="text-[#030A14] hover:underline font-medium">{t('login.terms')}</a>{" "}
              &{" "}
              <a href="/privacy" className="text-[#030A14] hover:underline font-medium">{t('login.privacy')}</a>
            </p>
            <Button
              onClick={async () => { await signOut(); navigate('/login'); }}
              className="w-full bg-red-600 hover:bg-red-700 active:scale-95 text-white font-medium transition-all duration-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
